import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import "./Sentences.css";

const Sentences = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { sentences, updateSentenceCategory, loading } = useData();
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSentence, setEditingSentence] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleEditCategory = (category) => {
    setEditingCategory({ ...category });
  };

  const handleSaveCategory = async () => {
    try {
      await updateSentenceCategory(editingCategory.id, editingCategory);
      setEditingCategory(null);
    } catch (error) {
      alert("Failed to save changes. Please try again.");
    }
  };

  const handleEditSentence = (categoryId, sentenceIndex, sentence) => {
    setEditingSentence({ categoryId, sentenceIndex, ...sentence });
    // Reset recording state when starting to edit
    setRecordedAudio(null);
    setIsRecording(false);
    setMediaRecorder(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setRecordedAudio(url);
        // Stop all tracks to release the microphone
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Failed to access microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleSaveSentence = async () => {
    try {
      const category = sentences.find(
        (c) => c.id === editingSentence.categoryId
      );
      const updatedItems = [...category.items];
      updatedItems[editingSentence.sentenceIndex] = {
        japanese: editingSentence.japanese,
        romaji: editingSentence.romaji,
        english: editingSentence.english,
        audio: editingSentence.audio,
      };

      await updateSentenceCategory(category.id, {
        ...category,
        items: updatedItems,
      });

      setEditingSentence(null);
    } catch (error) {
      alert("Failed to save sentence. Please try again.");
    }
  };

  const playAudio = (audioUrl) => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch((e) => console.log("Error playing audio:", e));
    }
  };

  if (loading) {
    return (
      <div className="sentences">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="sentences">
      <div className="header">
        <button className="back-button" onClick={handleBackToHome}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>
      </div>

      <div className="main-content">
        <h1 className="title">Japanese Sentences</h1>

        <div className="sentence-categories">
          {sentences.map((category) => (
            <div key={category.id} className="sentence-card">
              <div className="sentence-card-header">
                <h2>
                  {editingCategory && editingCategory.id === category.id ? (
                    <input
                      type="text"
                      value={editingCategory.category}
                      onChange={(e) =>
                        setEditingCategory({
                          ...editingCategory,
                          category: e.target.value,
                        })
                      }
                      className="edit-input"
                    />
                  ) : (
                    category.category
                  )}
                </h2>
                {isAdmin && (
                  <div className="admin-controls">
                    {editingCategory && editingCategory.id === category.id ? (
                      <>
                        <button
                          onClick={handleSaveCategory}
                          className="save-btn"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingCategory(null)}
                          className="cancel-btn"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEditCategory(category)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="sentence-list">
                {category.items?.map((sentence, index) => (
                  <div key={index} className="sentence-item">
                    {editingSentence &&
                    editingSentence.categoryId === category.id &&
                    editingSentence.sentenceIndex === index ? (
                      <div className="sentence-edit">
                        <textarea
                          value={editingSentence.japanese}
                          onChange={(e) =>
                            setEditingSentence({
                              ...editingSentence,
                              japanese: e.target.value,
                            })
                          }
                          className="japanese-textarea"
                          placeholder="Japanese sentence"
                        />
                        <textarea
                          value={editingSentence.romaji}
                          onChange={(e) =>
                            setEditingSentence({
                              ...editingSentence,
                              romaji: e.target.value,
                            })
                          }
                          className="romaji-textarea"
                          placeholder="Romaji"
                        />
                        <textarea
                          value={editingSentence.english}
                          onChange={(e) =>
                            setEditingSentence({
                              ...editingSentence,
                              english: e.target.value,
                            })
                          }
                          className="english-textarea"
                          placeholder="English translation"
                        />
                        <input
                          type="url"
                          value={editingSentence.audio}
                          onChange={(e) =>
                            setEditingSentence({
                              ...editingSentence,
                              audio: e.target.value,
                            })
                          }
                          className="audio-input"
                          placeholder="MP3 URL"
                        />
                        <div className="audio-recording-controls">
                          {!isRecording ? (
                            <button
                              onClick={startRecording}
                              className="record-btn"
                              title="Start recording"
                            >
                              🎤 Record
                            </button>
                          ) : (
                            <button
                              onClick={stopRecording}
                              className="stop-btn"
                              title="Stop recording"
                            >
                              ⏹️ Stop
                            </button>
                          )}
                          {recordedAudio && (
                            <>
                              <audio
                                src={recordedAudio}
                                controls
                                className="audio-preview"
                              />
                              <button
                                onClick={() => {
                                  setEditingSentence({
                                    ...editingSentence,
                                    audio: recordedAudio,
                                  });
                                }}
                                className="use-recording-btn"
                                title="Use this recording"
                              >
                                ✓ Use Recording
                              </button>
                            </>
                          )}
                        </div>
                        <div className="sentence-controls">
                          <button
                            onClick={handleSaveSentence}
                            className="save-btn"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingSentence(null)}
                            className="cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="sentence-display">
                        <div className="sentence-content">
                          <div className="sentence-japanese">
                            {sentence.japanese}
                          </div>
                          <div className="sentence-romaji">
                            {sentence.romaji}
                          </div>
                          <div className="sentence-english">
                            {sentence.english}
                          </div>
                        </div>
                        <div className="sentence-controls">
                          {sentence.audio && (
                            <button
                              onClick={() => playAudio(sentence.audio)}
                              className="audio-btn"
                              title="Play pronunciation"
                            >
                              🔊
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() =>
                                handleEditSentence(category.id, index, sentence)
                              }
                              className="edit-sentence-btn"
                              title="Edit sentence"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Sentence Modal */}
      {editingSentence && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Sentence</h3>
            <div className="edit-form">
              <label>
                Japanese:
                <textarea
                  value={editingSentence.japanese}
                  onChange={(e) =>
                    setEditingSentence({
                      ...editingSentence,
                      japanese: e.target.value,
                    })
                  }
                  rows="2"
                />
              </label>
              <label>
                Romaji:
                <textarea
                  value={editingSentence.romaji}
                  onChange={(e) =>
                    setEditingSentence({
                      ...editingSentence,
                      romaji: e.target.value,
                    })
                  }
                  rows="2"
                />
              </label>
              <label>
                English:
                <textarea
                  value={editingSentence.english}
                  onChange={(e) =>
                    setEditingSentence({
                      ...editingSentence,
                      english: e.target.value,
                    })
                  }
                  rows="2"
                />
              </label>
              <label>
                Audio URL (MP3):
                <input
                  type="url"
                  value={editingSentence.audio}
                  onChange={(e) =>
                    setEditingSentence({
                      ...editingSentence,
                      audio: e.target.value,
                    })
                  }
                  placeholder="https://example.com/audio.mp3"
                />
              </label>
              <div className="audio-recording-section">
                <label>Record Audio:</label>
                <div className="audio-recording-controls">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      className="record-btn"
                      title="Start recording"
                    >
                      🎤 Record
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="stop-btn"
                      title="Stop recording"
                    >
                      ⏹️ Stop
                    </button>
                  )}
                  {recordedAudio && (
                    <>
                      <audio
                        src={recordedAudio}
                        controls
                        className="audio-preview"
                      />
                      <button
                        onClick={() => {
                          setEditingSentence({
                            ...editingSentence,
                            audio: recordedAudio,
                          });
                        }}
                        className="use-recording-btn"
                        title="Use this recording"
                      >
                        ✓ Use Recording
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-controls">
                <button onClick={handleSaveSentence} className="save-btn">
                  Save
                </button>
                <button
                  onClick={() => setEditingSentence(null)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sentences;
